import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
  IconButton,
  alpha,
  Collapse,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Voicemail as VoicemailIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  ExitToApp as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  FiberManualRecord as DotIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Groups as GroupIcon,
  LocalOffer as TagIcon,
  Business,
  AccountTree,
  Schedule,
  VolumeUp,
  Queue,
  PhoneInTalk,
  Phonelink,
  RecordVoiceOver,
  Receipt,
  PhoneAndroid,
  Build,
  Inventory,
  History,
  SupervisorAccount,
  Delete,
  VpnKey,
  AccountBalance,
  MonetizationOn,
  Description as FileIcon,
  Mic as MicIcon,
  AddShoppingCart,
  Dns,
  CalendarMonth as CalendarIcon,
  Palette as PaletteIcon,
  Wifi,
  ManageAccounts,
  BugReport as BugReportIcon,
  Group as TeamIcon,
  Security,
  HelpOutline as HelpIcon,
  SwapHoriz as PortingIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import voicemailService from '../../services/voicemail.service';
import { useWebPhoneContext } from '../../contexts/WebPhoneContext';
// customerViewService import removed - using sessionStorage directly
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import brandingService from '../../services/branding.service';
import PresenceSelector from '../Common/PresenceSelector';
import CallingFromSelector from './CallingFromSelector';
import SMSFromSelector from './SMSFromSelector';
import errorLogger from '../../services/errorLogger.service';
import { HelpButton } from '../Help';

// Create logger for this component
const logger = errorLogger.createComponentLogger('Sidebar');

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  width: number;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, width }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const authOverride = useAuthWithCustomerOverride();
  const { logout } = useAuth();
  const member = authOverride.member;
  const isLoginAsCustomer = authOverride.isLoginAsCustomer;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [masterAdminExpanded, setMasterAdminExpanded] = useState(false);
  const [resellerAdminExpanded, setResellerAdminExpanded] = useState(false);
  // Master Admin submenus
  const [supportExpanded, setSupportExpanded] = useState(false);
  const [debugSettingsExpanded, setDebugSettingsExpanded] = useState(false);
  const [uiSettingsExpanded, setUiSettingsExpanded] = useState(false);
  const [billingExpanded, setBillingExpanded] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [otherExpanded, setOtherExpanded] = useState(false);
  const [unreadVoicemails, setUnreadVoicemails] = useState(0);
  const [branding, setBranding] = useState<any>(null);
  const customerFilter = useCustomerFilter();
  const webPhone = useWebPhoneContext();

  // Store reference to WebPhone V2 popup window
  const webPhoneV2WindowRef = useRef<Window | null>(null);
  // Store reference to WebPhone V3 popup window
  const webPhoneV3WindowRef = useRef<Window | null>(null);
  // Store reference to WebPhone V4 popup window
  const webPhoneV4WindowRef = useRef<Window | null>(null);

  // Fetch unread voicemail count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      const count = await voicemailService.getUnreadCount();
      setUnreadVoicemails(count);
    };

    // Initial fetch
    fetchUnreadCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    // Listen for voicemail updates
    const handleVoicemailUpdate = () => {
      fetchUnreadCount();
    };
    window.addEventListener('voicemail-updated', handleVoicemailUpdate);

    // Also refresh when location changes (especially when coming from voicemail page)
    return () => {
      clearInterval(interval);
      window.removeEventListener('voicemail-updated', handleVoicemailUpdate);
    };
  }, [location.pathname]);

  // Load branding
  useEffect(() => {
    const loadBranding = async () => {
      try {
        // Clear cache to force reload
        brandingService.clearCache();
        const brandingData = await brandingService.getCurrentBranding();
        logger.debug('Sidebar loaded branding:', brandingData);
        setBranding(brandingData);
      } catch (error) {
        logger.error('Failed to load branding:', error);
      }
    };
    loadBranding();
  }, []);

  // Removed auto-open WebPhone - users can manually open it from the sidebar

  // Reorganized menu items by category
  // Basic Configuration items
  const basicConfigItems = [
    // Hidden - kept for backwards compatibility
    // {
    //   text: 'Extensions-Old',
    //   icon: <PhoneIcon />,
    //   path: '/extensions',
    //   color: '#10b981',
    //   bgColor: '#d1fae5',
    // },
    {
      text: 'Extensions',
      icon: <PhoneIcon />,
      path: '/extensions-v2',
      color: '#10b981',
      bgColor: '#d1fae5',
    },
    {
      text: 'Users',
      icon: <PeopleIcon />,
      path: '/users',
      color: '#8b5cf6',
      bgColor: '#f3e8ff',
    },
    { 
      text: 'Phone Numbers', 
      icon: <TagIcon />, 
      path: '/phone-numbers',
      color: '#f59e0b',
      bgColor: '#fef3c7',
    },
    { 
      text: 'Softphones', 
      icon: <PhoneAndroid />, 
      path: '/softphones',
      color: '#7c3aed',
      bgColor: '#ede9fe',
    },
  ];

  // Call Routing & Features items
  const callRoutingItems = [
    { 
      text: 'Ring Groups', 
      icon: <GroupIcon />, 
      path: '/ring-groups',
      color: '#ec4899',
      bgColor: '#fce7f3',
    },
    { 
      text: 'IVR Menus', 
      icon: <AccountTree />, 
      path: '/ivr-menus',
      color: '#14b8a6',
      bgColor: '#ccfbf1',
    },
    { 
      text: 'Time Conditions', 
      icon: <Schedule />, 
      path: '/time-conditions',
      color: '#3b82f6',
      bgColor: '#dbeafe',
    },
  ];

  // Prompts items
  const promptsItems = [
    { 
      text: 'Prompts', 
      icon: <RecordVoiceOver />, 
      path: '/prompts',
      color: '#f59e0b',
      bgColor: '#fef3c7',
    },
  ];

  // Call Queuing items
  const callQueuingItems = [
    { 
      text: 'Queuing', 
      icon: <Queue />, 
      path: '/call-queue',
      color: '#06b6d4',
      bgColor: '#cffafe',
    },
    { 
      text: 'Queue Prompts', 
      icon: <VolumeUp />, 
      path: '/queue-announcements',
      color: '#84cc16',
      bgColor: '#ecfccb',
    },
  ];

  // Reporting & Monitoring items
  const reportingItems = [
    {
      text: 'Call Log',
      icon: <DashboardIcon />,
      path: '/call-log',
      color: '#6366f1',
      bgColor: '#e0e7ff',
    },
    {
      text: 'Recording Settings',
      icon: <MicIcon />,
      path: '/recording-settings',
      color: '#ef4444',
      bgColor: '#fee2e2',
    },
  ];

  // System & Billing items
  const systemItems = [
    { 
      text: 'Hardware', 
      icon: <Phonelink />, 
      path: '/hardware',
      color: '#0891b2',
      bgColor: '#cffafe',
    },
    { 
      text: 'Billing', 
      icon: <Receipt />, 
      path: '/billing',
      color: '#dc2626',
      bgColor: '#fef2f2',
    },
  ];

  // Master Admin only items
  const masterOnlyItems = [
    {
      text: 'Customers',
      icon: <Business />,
      path: '/customers',
      color: '#3b82f6',
      bgColor: '#dbeafe',
    },
    {
      text: 'Extensions (New)',
      icon: <PhoneIcon />,
      path: '/extensions-quota',
      color: '#10b981',
      bgColor: '#d1fae5',
    },
    {
      text: 'Issues Tracking',
      icon: <BugReportIcon />,
      path: '/issues-tracking',
      color: '#b91c1c',
      bgColor: '#fee2e2',
    },
    {
      text: 'Error Logs',
      icon: <BugReportIcon />,
      path: '/error-logs',
      color: '#dc2626',
      bgColor: '#fef2f2',
    },
    {
      text: 'Debug Settings',
      icon: <SettingsIcon />,
      path: '/debug-settings',
      color: '#f97316',
      bgColor: '#fff7ed',
    },
    {
      text: 'WebPhone V2',
      icon: <PhoneIcon />,
      path: '/__webphone_v2__',
      color: '#3b82f6',
      bgColor: '#dbeafe',
      isWebPhoneV2: true,
    },
    {
      text: 'PCI Compliance',
      icon: <Security />,
      path: '/pci-compliance',
      color: '#10b981',
      bgColor: '#d1fae5',
    },
    { 
      text: 'Post Payment', 
      icon: <AccountBalance />, 
      path: '/billing/post-payment',
      color: '#059669',
      bgColor: '#d1fae5',
    },
    { 
      text: 'PAD Files', 
      icon: <FileIcon />, 
      path: '/billing/pad-files',
      color: '#0891b2',
      bgColor: '#cffafe',
    },
    { 
      text: 'Service Packages', 
      icon: <Inventory />, 
      path: '/service-packages',
      color: '#ea580c',
      bgColor: '#ffedd5',
    },
    { 
      text: 'Add-On Services', 
      icon: <AddShoppingCart />, 
      path: '/addon-services',
      color: '#7c3aed',
      bgColor: '#ede9fe',
    },
    {
      text: 'Scheduled Tasks',
      icon: <Schedule />,
      path: '/scheduled-tasks',
      color: '#0891b2',
      bgColor: '#cffafe',
    },
    {
      text: 'Signup Manager',
      icon: <ManageAccounts />,
      path: '/signup-manager',
      color: '#7c3aed',
      bgColor: '#ede9fe',
    },
    { 
      text: 'Internet Services', 
      icon: <Wifi />, 
      path: '/internet-services',
      color: '#0ea5e9',
      bgColor: '#e0f2fe',
    },
    { 
      text: 'Internet Vendors', 
      icon: <ManageAccounts />, 
      path: '/internet-vendors',
      color: '#10b981',
      bgColor: '#d1fae5',
    },
    { 
      text: 'Bundle Management', 
      icon: <GroupIcon />, 
      path: '/bundle-management',
      color: '#f97316',
      bgColor: '#fed7aa',
    },
    { 
      text: 'System Settings', 
      icon: <Build />, 
      path: '/system-settings',
      color: '#ef4444',
      bgColor: '#fee2e2',
    },
    { 
      text: 'Partner Management', 
      icon: <SupervisorAccount />, 
      path: '/reseller-management',
      color: '#0891b2',
      bgColor: '#cffafe',
    },
    { 
      text: 'DID Deletion', 
      icon: <Delete />, 
      path: '/did-deletion',
      color: '#dc2626',
      bgColor: '#fee2e2',
    },
    { 
      text: 'Dunning Management', 
      icon: <AccountBalance />, 
      path: '/dunning-management',
      color: '#f59e0b',
      bgColor: '#fef3c7',
    },
    { 
      text: 'Brand Management', 
      icon: <PaletteIcon />, 
      path: '/brand-management',
      color: '#ec4899',
      bgColor: '#fce7f3',
    },
    { 
      text: 'API Key Management', 
      icon: <VpnKey />, 
      path: '/api-keys',
      color: '#7c3aed',
      bgColor: '#ede9fe',
    },
    { 
      text: 'Server Config', 
      icon: <Dns />, 
      path: '/server-config',
      color: '#059669',
      bgColor: '#d1fae5',
    },
  ];

  // Reseller Admin only items (specific features for resellers)
  const resellerOnlyItems = [
    { 
      text: 'My Customers', 
      icon: <Business />, 
      path: '/reseller/customers',
      color: '#3b82f6',
      bgColor: '#dbeafe',
    },
    { 
      text: 'Customer Billing', 
      icon: <AccountBalance />, 
      path: '/billing/post-payment',
      color: '#059669',
      bgColor: '#d1fae5',
    },
  ];

  // Combine all admin items for master admin
  const masterAdminItems = [
    ...basicConfigItems,
    ...callRoutingItems,
    ...promptsItems,
    ...callQueuingItems,
    ...reportingItems,
    ...systemItems,
    ...masterOnlyItems,
  ];

  // Reseller admin has same items but no master-only features
  const resellerAdminItems = [
    ...basicConfigItems,
    ...callRoutingItems,
    ...promptsItems,
    ...callQueuingItems,
    ...reportingItems,
    ...systemItems,
  ];

  // Determine which admin items to show
  let adminMenuItems: typeof masterAdminItems = [];
  if (member?.memberType === 'master_admin') {
    adminMenuItems = masterAdminItems;
  } else if (member?.memberType === 'reseller_admin') {
    adminMenuItems = resellerAdminItems;
  } else if (member?.memberType === 'admin') {
    // Regular customer admins get basic admin items
    adminMenuItems = [...basicConfigItems, ...callRoutingItems];
  }

  const regularMenuItems = [
    {
      text: 'WebPhone',
      icon: <PhoneIcon />,
      path: '/__webphone_v3__',
      color: '#6366f1',
      bgColor: '#e0e7ff',
      isWebPhoneV3: true,
    },
    {
      text: 'WebPhone V4 (Beta)',
      icon: <PhoneIcon />,
      path: '/__webphone_v4__',
      color: '#0ea5e9',
      bgColor: '#cffafe',
      isWebPhoneV4: true,
      masterAdminOnly: true,
    },
    {
      text: 'Voicemail',
      icon: <VoicemailIcon />,
      path: '/voicemail',
      color: '#8b5cf6',
      bgColor: '#f3e8ff',
      badge: unreadVoicemails,
    },
    {
      text: 'Team',
      icon: <TeamIcon />,
      path: '/team',
      color: '#ec4899',
      bgColor: '#fce7f3',
    },
    {
      text: 'My Call Log',
      icon: <History />,
      path: '/my-calls',
      color: '#f59e0b',
      bgColor: '#fef3c7',
    },
    {
      text: 'Calendar',
      icon: <CalendarIcon />,
      path: '/calendar',
      color: '#10b981',
      bgColor: '#d1fae5',
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      color: '#6366f1',
      bgColor: '#e0e7ff',
    },
    {
      text: 'Profile',
      icon: <PersonIcon />,
      path: '/profile',
      color: '#06b6d4',
      bgColor: '#e0f2fe',
    },
    {
      text: 'Help Center',
      icon: <HelpIcon />,
      path: '/__help__',  // Special path that won't match any route
      color: '#f97316',
      bgColor: '#ffedd5',
      isHelp: true,  // Special flag to identify this item
    },
  ];


  const handleNavigation = (path: string) => {
    if (isMobile) {
      onClose();
    }
    // Small delay to allow drawer to close before navigation
    setTimeout(() => {
      const params = new URLSearchParams();
      
      // Preserve customer filter parameters when navigating (but not login as customer, which uses session storage)
      if (customerFilter.isFiltered && !isLoginAsCustomer) {
        params.set('customerId', customerFilter.customerId!);
        params.set('customerName', customerFilter.customerName || '');
      }
      
      const queryString = params.toString();
      navigate(queryString ? `${path}?${queryString}` : path);
    }, isMobile ? 200 : 0);
  };

  const handleLogout = async () => {
    await logout();
  };

  // Helper function to render menu items - defined before return statement
  const renderMenuItem = (item: any, indent: number = 0) => {
    // Check if item has role restrictions
    if (item.roles && !item.roles.includes(member?.memberType)) {
      return null;
    }
    
    const isSelected = location.pathname === item.path;
    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.5, pl: indent }}>
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          sx={{
            borderRadius: '12px',
            mx: 0.5,
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            overflow: 'hidden',
            ...(isSelected
              ? {
                  backgroundColor: alpha(item.color, 0.1),
                  color: item.color,
                  '&:hover': {
                    backgroundColor: alpha(item.color, 0.15),
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '4px',
                    height: '70%',
                    backgroundColor: item.color,
                    borderRadius: '0 4px 4px 0',
                  },
                }
              : {
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateX(4px)',
                    '& .menu-icon': {
                      transform: 'scale(1.1)',
                    },
                  },
                }),
          }}
        >
          <ListItemIcon
            className="menu-icon"
            sx={{
              minWidth: 35,
              color: isSelected ? item.color : theme.palette.text.secondary,
              transition: 'all 0.2s ease-in-out',
              '& svg': {
                fontSize: '1.25rem',
              },
            }}
          >
            <Box
              sx={{
                width: 35,
                height: 35,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isSelected ? item.bgColor : 'transparent',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {item.icon}
            </Box>
          </ListItemIcon>
          <Box
            sx={{
              backgroundColor: alpha(item.color, 0.08),
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              flex: 1,
              mr: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.813rem',
                fontWeight: isSelected ? 600 : 400,
                color: item.color,
              }}
            >
              {item.text}
            </Typography>
          </Box>
          {isSelected && (
            <DotIcon 
              sx={{ 
                fontSize: '0.5rem', 
                color: item.color,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.4 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.4 },
                },
              }} 
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        width: width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.default,
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: 64,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <img
          key={branding?.logo_url || "default-logo"}
          src={branding?.logo_url || "/OBPLogo1.png"}
          alt="Logo"
          style={{
            height: '45px',
            width: 'auto',
            display: 'block',
            margin: isMobile ? '0 auto' : '0',
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HelpButton />
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.1),
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* Presence Selector */}
      <Box sx={{ px: 2, py: 1 }}>
        <PresenceSelector />
      </Box>

      {/* Calling From Selector */}
      <Box sx={{ px: 2, py: 1 }}>
        <CallingFromSelector />
      </Box>

      {/* SMS From Selector */}
      <Box sx={{ px: 2, py: 1 }}>
        <SMSFromSelector />
      </Box>

      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {/* Master Administrator Section - Only for master_admin */}
        {member?.memberType === 'master_admin' && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setMasterAdminExpanded(!masterAdminExpanded)}
                sx={{
                  borderRadius: '12px',
                  mx: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: alpha(theme.palette.error.main, 0.04),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.error.main }}>
                  <SupervisorAccount />
                </ListItemIcon>
                <ListItemText 
                  primary="Master Administrator" 
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                />
                {masterAdminExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={masterAdminExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* Customers - direct item */}
                {renderMenuItem({
                  text: 'Customers',
                  icon: <Business />,
                  path: '/customers',
                  color: '#3b82f6',
                  bgColor: '#dbeafe',
                }, 2)}

                {/* Extensions (New) - direct item */}
                {renderMenuItem({
                  text: 'Extensions (New)',
                  icon: <PhoneIcon />,
                  path: '/extensions-quota',
                  color: '#10b981',
                  bgColor: '#d1fae5',
                }, 2)}

                {/* Signup Manager - direct item */}
                {renderMenuItem({
                  text: 'Signup Manager',
                  icon: <Business />,
                  path: '/signup-manager',
                  color: '#10b981',
                  bgColor: '#d1fae5',
                }, 2)}

                {/* Activity Log - direct item */}
                {renderMenuItem({
                  text: 'Activity Log',
                  icon: <History />,
                  path: '/activity-log',
                  color: '#0ea5e9',
                  bgColor: '#e0f2fe',
                }, 2)}

                {/* Number Porting - direct item */}
                {renderMenuItem({
                  text: 'Number Porting',
                  icon: <PortingIcon />,
                  path: '/porting',
                  color: '#8b5cf6',
                  bgColor: '#f3e8ff',
                }, 2)}

                {/* Support Submenu */}
                <ListItem disablePadding sx={{ pl: 4 }}>
                  <ListItemButton
                    onClick={() => setSupportExpanded(!supportExpanded)}
                    sx={{ borderRadius: '8px' }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <BugReportIcon />
                    </ListItemIcon>
                    <ListItemText primary="Support" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    {supportExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={supportExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderMenuItem({
                      text: 'Issues Tracking',
                      icon: <BugReportIcon />,
                      path: '/issues-tracking',
                      color: '#b91c1c',
                      bgColor: '#fee2e2',
                    }, 3)}
                    {renderMenuItem({
                      text: 'Activity Logs',
                      icon: <History />,
                      path: '/activity-log',
                      color: '#0ea5e9',
                      bgColor: '#e0f2fe',
                    }, 3)}

                    {/* Debug Settings Submenu */}
                    <ListItem disablePadding sx={{ pl: 6 }}>
                      <ListItemButton
                        onClick={() => setDebugSettingsExpanded(!debugSettingsExpanded)}
                        sx={{ borderRadius: '8px' }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <BugReportIcon />
                        </ListItemIcon>
                        <ListItemText primary="Debug" primaryTypographyProps={{ fontSize: '0.813rem' }} />
                        {debugSettingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={debugSettingsExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {renderMenuItem({
                          text: 'Error Logs',
                          icon: <BugReportIcon />,
                          path: '/error-logs',
                          color: '#dc2626',
                          bgColor: '#fef2f2',
                        }, 4)}
                        {renderMenuItem({
                          text: 'Debug Settings',
                          icon: <SettingsIcon />,
                          path: '/debug-settings',
                          color: '#f97316',
                          bgColor: '#fff7ed',
                        }, 4)}
                      </List>
                    </Collapse>

                    {/* UI Settings Submenu */}
                    <ListItem disablePadding sx={{ pl: 6 }}>
                      <ListItemButton
                        onClick={() => setUiSettingsExpanded(!uiSettingsExpanded)}
                        sx={{ borderRadius: '8px' }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="UI Settings" primaryTypographyProps={{ fontSize: '0.813rem' }} />
                        {uiSettingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={uiSettingsExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {renderMenuItem({
                          text: 'PCI Compliance',
                          icon: <Security />,
                          path: '/pci-compliance',
                          color: '#10b981',
                          bgColor: '#d1fae5',
                        }, 4)}
                      </List>
                    </Collapse>
                  </List>
                </Collapse>
                
                {/* Billing Submenu */}
                <ListItem disablePadding sx={{ pl: 4 }}>
                  <ListItemButton
                    onClick={() => setBillingExpanded(!billingExpanded)}
                    sx={{ borderRadius: '8px' }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AccountBalance />
                    </ListItemIcon>
                    <ListItemText primary="Billing" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    {billingExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={billingExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderMenuItem({ 
                      text: 'Post Payment', 
                      icon: <AccountBalance />, 
                      path: '/billing/post-payment',
                      color: '#059669',
                      bgColor: '#d1fae5',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'PAD Files', 
                      icon: <FileIcon />, 
                      path: '/billing/pad-files',
                      color: '#0891b2',
                      bgColor: '#cffafe',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'Dunning Management', 
                      icon: <MonetizationOn />, 
                      path: '/dunning-management',
                      color: '#dc2626',
                      bgColor: '#fee2e2',
                    }, 3)}
                  </List>
                </Collapse>
                
                {/* Services Submenu */}
                <ListItem disablePadding sx={{ pl: 4 }}>
                  <ListItemButton
                    onClick={() => setServicesExpanded(!servicesExpanded)}
                    sx={{ borderRadius: '8px' }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Inventory />
                    </ListItemIcon>
                    <ListItemText primary="Services" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    {servicesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={servicesExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderMenuItem({ 
                      text: 'Service Packages', 
                      icon: <Inventory />, 
                      path: '/service-packages',
                      color: '#ea580c',
                      bgColor: '#ffedd5',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'Add-On Services', 
                      icon: <AddShoppingCart />, 
                      path: '/addon-services',
                      color: '#7c3aed',
                      bgColor: '#ede9fe',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'Internet Services', 
                      icon: <Wifi />, 
                      path: '/internet-services',
                      color: '#0ea5e9',
                      bgColor: '#e0f2fe',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'Internet Vendors', 
                      icon: <ManageAccounts />, 
                      path: '/internet-vendors',
                      color: '#10b981',
                      bgColor: '#d1fae5',
                    }, 3)}
                  </List>
                </Collapse>
                
                {/* Other Submenu */}
                <ListItem disablePadding sx={{ pl: 4 }}>
                  <ListItemButton
                    onClick={() => setOtherExpanded(!otherExpanded)}
                    sx={{ borderRadius: '8px' }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Other" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    {otherExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={otherExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderMenuItem({ 
                      text: 'Partner Management', 
                      icon: <SupervisorAccount />, 
                      path: '/reseller-management',
                      color: '#0891b2',
                      bgColor: '#cffafe',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'Brand Management', 
                      icon: <PaletteIcon />, 
                      path: '/brand-management',
                      color: '#ec4899',
                      bgColor: '#fce7f3',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'DID Deletion', 
                      icon: <Delete />, 
                      path: '/did-deletion',
                      color: '#dc2626',
                      bgColor: '#fee2e2',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'API Key Management', 
                      icon: <VpnKey />, 
                      path: '/api-keys',
                      color: '#7c3aed',
                      bgColor: '#ede9fe',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'Server Config', 
                      icon: <Dns />, 
                      path: '/server-config',
                      color: '#059669',
                      bgColor: '#d1fae5',
                    }, 3)}
                    {renderMenuItem({ 
                      text: 'PCI Compliance', 
                      icon: <Security />, 
                      path: '/pci-compliance',
                      color: '#10b981',
                      bgColor: '#d1fae5',
                    }, 3)}
                    {renderMenuItem({
                      text: 'Scheduled Tasks',
                      icon: <Schedule />,
                      path: '/scheduled-tasks',
                      color: '#0891b2',
                      bgColor: '#cffafe',
                    }, 3)}
                    {renderMenuItem({
                      text: 'Signup Manager',
                      icon: <ManageAccounts />,
                      path: '/signup-manager',
                      color: '#7c3aed',
                      bgColor: '#ede9fe',
                    }, 3)}
                  </List>
                </Collapse>
              </List>
            </Collapse>
            <Divider sx={{ my: 1.5, mx: 2, opacity: 0.5 }} />
          </>
        )}
        
        {/* Reseller Administrator Section - For reseller_admin and master_admin (for management) */}
        {(member?.memberType === 'reseller_admin' || member?.memberType === 'master_admin') && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setResellerAdminExpanded(!resellerAdminExpanded)}
                sx={{
                  borderRadius: '12px',
                  mx: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: alpha(theme.palette.success.main, 0.04),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.success.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.success.main }}>
                  <ManageAccounts />
                </ListItemIcon>
                <ListItemText 
                  primary="Reseller Administrator" 
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                />
                {resellerAdminExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={resellerAdminExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {resellerOnlyItems.map((item) => renderMenuItem(item, 2))}
              </List>
            </Collapse>
            <Divider sx={{ my: 1.5, mx: 2, opacity: 0.5 }} />
          </>
        )}
        
        {adminMenuItems.length > 0 && (
          <>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setAdminExpanded(!adminExpanded)}
                sx={{
                  borderRadius: '12px',
                  mx: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: alpha(theme.palette.warning.main, 0.04),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.warning.main }}>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Admin Tools" 
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                />
                {adminExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* Basic Configuration */}
                <ListItem sx={{ pl: 3, pb: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    BASIC CONFIGURATION
                  </Typography>
                </ListItem>
                {basicConfigItems.map((item) => renderMenuItem(item, 2))}
                
                {/* Call Routing & Features */}
                <ListItem sx={{ pl: 3, pb: 0, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    CALL ROUTING & FEATURES
                  </Typography>
                </ListItem>
                {callRoutingItems.map((item) => renderMenuItem(item, 2))}
                
                {/* Prompts */}
                <ListItem sx={{ pl: 3, pb: 0, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    PROMPTS
                  </Typography>
                </ListItem>
                {promptsItems.map((item) => renderMenuItem(item, 2))}
                
                {/* Call Queuing */}
                <ListItem sx={{ pl: 3, pb: 0, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    CALL QUEUING
                  </Typography>
                </ListItem>
                {callQueuingItems.map((item) => renderMenuItem(item, 2))}
                
                {/* Reporting & Monitoring */}
                <ListItem sx={{ pl: 3, pb: 0, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    REPORTING & MONITORING
                  </Typography>
                </ListItem>
                {reportingItems.map((item) => renderMenuItem(item, 2))}
                
                {/* System & Billing */}
                <ListItem sx={{ pl: 3, pb: 0, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    SYSTEM & BILLING
                  </Typography>
                </ListItem>
                {systemItems.map((item) => renderMenuItem(item, 2))}
              </List>
            </Collapse>
            <Divider sx={{ my: 1.5, mx: 2, opacity: 0.5 }} />
          </>
        )}
        {regularMenuItems
          .filter(item => {
            // Filter out masterAdminOnly items for non-master_admin users
            if ((item as any).masterAdminOnly && member?.memberType !== 'master_admin') {
              return false;
            }
            return true;
          })
          .map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  if ((item as any).isHelp) {
                    // Open help center via custom event
                    window.dispatchEvent(new CustomEvent('openHelp'));
                  } else if ((item as any).isWebPhone) {
                    // Open WebPhone in Picture-in-Picture mode
                    webPhone.openPictureInPicture();
                  } else if ((item as any).isWebPhoneV2) {
                    // Open or focus WebPhone V2 popup window
                    // Check if window is still open and valid
                    if (webPhoneV2WindowRef.current && !webPhoneV2WindowRef.current.closed) {
                      // Window exists and is open, just focus it
                      webPhoneV2WindowRef.current.focus();
                    } else {
                      // Window doesn't exist or was closed, create new one
                      const width = 340;
                      const height = 600;
                      const left = window.screen.width - width - 20;
                      const top = 80;
                      const popupWindow = window.open(
                        '/webphone-popup-v2',
                        'WebPhoneV2',
                        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
                      );
                      webPhoneV2WindowRef.current = popupWindow;
                    }
                  } else if ((item as any).isWebPhoneV3) {
                    // Open or focus WebPhone V3 popup window
                    // Check if window is still open and valid
                    if (webPhoneV3WindowRef.current && !webPhoneV3WindowRef.current.closed) {
                      // Window exists and is open, just focus it
                      webPhoneV3WindowRef.current.focus();
                    } else {
                      // Window doesn't exist or was closed, create new one
                      const width = 340;
                      const height = 600;
                      const left = window.screen.width - width - 20;
                      const top = 80;
                      const popupWindow = window.open(
                        '/webphone-popup-v3',
                        'WebPhoneV3',
                        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
                      );
                      webPhoneV3WindowRef.current = popupWindow;
                    }
                  } else if ((item as any).isWebPhoneV4) {
                    if (webPhoneV4WindowRef.current && !webPhoneV4WindowRef.current.closed) {
                      webPhoneV4WindowRef.current.focus();
                    } else {
                      const width = 340;
                      const height = 600;
                      const left = window.screen.width - width - 20;
                      const top = 80;
                      const popupWindow = window.open(
                        '/webphone-popup-v4',
                        'WebPhoneV4',
                        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
                      );
                      webPhoneV4WindowRef.current = popupWindow;
                    }
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  borderRadius: '12px',
                  mb: 0.5,
                  mx: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  overflow: 'hidden',
                  ...(isSelected
                    ? {
                        backgroundColor: alpha(item.color, 0.1),
                        color: item.color,
                        '&:hover': {
                          backgroundColor: alpha(item.color, 0.15),
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '4px',
                          height: '70%',
                          backgroundColor: item.color,
                          borderRadius: '0 4px 4px 0',
                        },
                      }
                    : {
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          transform: 'translateX(4px)',
                          '& .menu-icon': {
                            transform: 'scale(1.1)',
                          },
                        },
                      }),
                }}
              >
                <ListItemIcon
                  className="menu-icon"
                  sx={{
                    minWidth: 35,
                    color: isSelected ? item.color : theme.palette.text.secondary,
                    transition: 'all 0.2s ease-in-out',
                    '& svg': {
                      fontSize: '1.25rem',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 35,
                      height: 35,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isSelected ? item.bgColor : 'transparent',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {item.badge !== undefined && item.badge > 0 ? (
                      <Badge badgeContent={item.badge} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </Box>
                </ListItemIcon>
                <Box
                  sx={{
                    backgroundColor: alpha(item.color, 0.08),
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '8px',
                    flex: 1,
                    mr: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.813rem',
                      fontWeight: isSelected ? 600 : 400,
                      color: item.color,
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
                {isSelected && (
                  <DotIcon 
                    sx={{ 
                      fontSize: '0.5rem', 
                      color: item.color,
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 0.4 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.4 },
                      },
                    }} 
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ mx: 2 }} />
      
      {member && (
        <Box sx={{ p: 1.5 }}>
          {isLoginAsCustomer && (
            <ListItemButton 
              onClick={() => {
                // Clear the customer view from sessionStorage (tab-specific)
                sessionStorage.removeItem('customerView');
                // Force a page reload to reset all state
                window.location.href = '/customers';
              }}
              sx={{
                borderRadius: '10px',
                py: 1,
                px: 1.5,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.2),
                },
              }}
            >
              <LogoutIcon 
                sx={{ 
                  fontSize: '1.1rem',
                  color: theme.palette.warning.main,
                  mr: 1,
                  transform: 'rotate(180deg)',
                }} 
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.813rem',
                  fontWeight: 500,
                  color: theme.palette.warning.main,
                }}
              >
                Exit Customer View
              </Typography>
            </ListItemButton>
          )}
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              borderRadius: '10px',
              py: 1,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: alpha(theme.palette.grey[500], 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                '& .logout-icon': {
                  color: theme.palette.error.main,
                },
              },
            }}
          >
            <Avatar 
              sx={{ 
                width: 28, 
                height: 28, 
                mr: 1,
                fontSize: '0.75rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                fontWeight: 600,
              }}
            >
              {member.firstname[0]}{member.lastname[0]}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="body2" 
                noWrap 
                sx={{ 
                  fontSize: '0.813rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {member.firstname} {member.lastname}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                noWrap
                sx={{ 
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                }}
              >
                {isLoginAsCustomer && member && 'loginAsCustomerName' in member && member.loginAsCustomerName ? (
                  <Box component="span" sx={{ color: theme.palette.warning.main }}>
                    Viewing as: {member.loginAsCustomerName}
                  </Box>
                ) : (
                  member?.customerName
                )}
              </Typography>
            </Box>
            <LogoutIcon 
              className="logout-icon"
              sx={{ 
                fontSize: '1.1rem',
                color: theme.palette.text.secondary,
                transition: 'color 0.2s',
              }} 
            />
          </ListItemButton>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
